import { Link, Navigate, useParams } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { useSEO } from "@/hooks/useSEO";
import { ARTICLES, getArticle } from "@/data/articles";
import { SITE_URL } from "@/data/articlesPart1";
import { APP_URL } from "@/lib/constants";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useSEO({
    title: article ? article.seoTitle : "Статья не найдена | Angiolini",
    description: article ? article.description : "",
    canonical: `${SITE_URL}/articles/${slug}`,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          image: `${SITE_URL}${article.image}`,
          datePublished: article.date,
          inLanguage: "ru",
          author: { "@type": "Organization", name: "Angiolini" },
          publisher: {
            "@type": "Organization",
            name: "Angiolini",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` },
          },
          mainEntityOfPage: `${SITE_URL}/articles/${slug}`,
        }
      : null,
  });

  if (!article) return <Navigate to="/articles" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <main data-testid="article-page">
      <Navbar solid />
      <section className="pt-40 pb-16" style={{ background: "var(--ink)" }}>
        <div className="pw-container max-w-[900px]">
          <Link to="/articles" data-testid="back-to-articles" className="text-[13px] font-medium text-white/50 hover:text-white transition-colors duration-300">
            ← Все статьи
          </Link>
          <div className="mt-8 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.14em]">
            <span style={{ color: "var(--accent)" }}>{article.tag}</span>
            <span className="text-white/40">·</span>
            <span className="text-white/40">{new Date(article.date).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="text-white/40">·</span>
            <span className="text-white/40">{article.readTime}</span>
          </div>
          <h1
            className="mt-6 text-white font-semibold"
            style={{ fontSize: "clamp(1.9rem, 4vw, 3.25rem)", letterSpacing: "-0.04em", lineHeight: 1.1 }}
          >
            {article.title}
          </h1>
        </div>
      </section>

      <section style={{ background: "var(--paper)" }}>
        <div className="pw-container max-w-[900px]">
          <div className="rounded-2xl overflow-hidden -mt-0 translate-y-[-40px] aspect-[16/9]">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" style={{ objectPosition: "center 25%" }} />
          </div>

          <article className="pb-16 max-w-[720px]">
            {article.sections.map((s, i) => (
              <section key={i} className="mt-10 first:mt-2">
                <h2 className="text-xl md:text-2xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
                  {s.h}
                </h2>
                {s.ps.map((p, j) => (
                  <p key={j} className="mt-4 text-[16px] leading-[1.75]" style={{ color: "var(--ink-3)" }}>
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </article>

          <div
            className="mb-20 p-9 md:p-12 rounded-2xl flex flex-col md:flex-row md:items-center md:justify-between gap-6"
            style={{ background: "var(--ink)" }}
          >
            <div>
              <h3 className="text-white text-xl md:text-2xl font-semibold" style={{ letterSpacing: "-0.02em" }}>
                Откройте коллекцию Angiolini
              </h3>
              <p className="mt-2 text-white/55 text-[14px]">
                Каталог, цены, ИИ-примерка и 10 000 ₸ бонусов за регистрацию.
              </p>
            </div>
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-cta"
              className="pw-btn pw-btn-light shrink-0"
            >
              Открыть приложение
            </a>
          </div>
        </div>
      </section>

      <section className="pb-24" style={{ background: "var(--paper)" }}>
        <div className="pw-container">
          <h2 className="pw-h2 !text-[1.75rem] md:!text-[2.25rem] mb-10">Читайте также.</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((a) => (
              <Link key={a.slug} to={`/articles/${a.slug}`} className="group block">
                <div className="rounded-2xl overflow-hidden aspect-[16/10]">
                  <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" style={{ objectPosition: "center 28%" }} />
                </div>
                <h3 className="mt-4 text-[16px] font-semibold leading-snug" style={{ letterSpacing: "-0.01em" }}>
                  {a.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
